import { Button, CircularProgress, Container, Divider, Grid, MenuItem, TextField, Typography, Box, Paper, IconButton, alpha } from "@mui/material";
import { Close, PersonAddAlt1 } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

// Users Update Component (Add User)
const UsersUpdate = ({ handleModalClose, refetch }) => {
    // SNACKBAR
    const { enqueueSnackbar } = useSnackbar();
    const axiosPrivate = useAxiosPrivate();

    // HOOK FORM
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ mode: "onChange" });

    // MUTATION & QUERY
    const { mutate, data, error, isLoading } = useMutation((formData) => {
        // Feature toggle: default role is "test" for now as per legacy code
        const dataSent = { ...formData, role: "test" };
        return axiosPrivate.post(`/users`, dataSent);
    });

    const { data: roles } = useQuery(["get_all_roles"], () => {
        return axiosPrivate.get(`/roles`, { params: { page: -1 } });
    }, { refetchOnWindowFocus: false });

    // USEFFECTS
    useEffect(() => {
        if (data) {
            enqueueSnackbar(`User added successfully!`, { variant: "success", autoHideDuration: 2000 });
            refetch();
            reset();
            handleModalClose();
        }
    }, [data, enqueueSnackbar, handleModalClose, refetch, reset]);

    useEffect(() => {
        if (error) {
            const fieldErrors = error?.response?.data?.error?.field_error;
            if (fieldErrors?.length > 0) {
                fieldErrors.forEach((msg) => {
                    enqueueSnackbar(msg.description || "Field error", { variant: "error" });
                });
            } else {
                enqueueSnackbar(error?.response?.data?.error?.message || error?.message || "Network Error!", { variant: "error" });
            }
        }
    }, [enqueueSnackbar, error]);

    // RENDER
    return (
        <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '440px' },
                    borderRadius: { xs: '24px 24px 0 0', sm: 6 },
                    boxShadow: theme => theme.palette.mode === 'light'
                        ? "0px 20px 60px rgba(0, 0, 0, 0.15)"
                        : `0px 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                <IconButton
                    onClick={handleModalClose}
                    sx={{ position: 'absolute', right: 16, top: 16, color: 'text.disabled' }}
                >
                    <Close />
                </IconButton>

                <Box sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ p: 2, borderRadius: 4, bgcolor: 'primary.main', display: 'flex', boxShadow: theme => `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`, mb: 2 }}>
                            <PersonAddAlt1 sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: '-0.5px' }}>
                            Add New User
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            Create a new identity profile for access.
                        </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit((data) => mutate(data))}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Controller
                                    name="first_name"
                                    control={control}
                                    rules={{ required: "First name is required" }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="First Name"
                                            error={!!errors?.first_name}
                                            helperText={errors?.first_name?.message}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3.5 } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="middle_name"
                                    control={control}
                                    rules={{ required: "Middle name is required" }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Middle Name"
                                            error={!!errors?.middle_name}
                                            helperText={errors?.middle_name?.message}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3.5 } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="last_name"
                                    control={control}
                                    rules={{ required: "Last name is required" }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Last Name"
                                            error={!!errors?.last_name}
                                            helperText={errors?.last_name?.message}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3.5 } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Controller
                                    name="gender"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            select
                                            size="small"
                                            label="Gender"
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3.5 } }}
                                        >
                                            <MenuItem value="male">Male</MenuItem>
                                            <MenuItem value="female">Female</MenuItem>
                                        </TextField>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Controller
                                    name="phone"
                                    control={control}
                                    rules={{ required: "Required" }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Phone"
                                            error={!!errors?.phone}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3.5 } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="email"
                                    control={control}
                                    rules={{ required: "Email is required" }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Email Address"
                                            error={!!errors?.email}
                                            helperText={errors?.email?.message}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3.5 } }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="inherit"
                                onClick={handleModalClose}
                                sx={{ borderRadius: 3.5, textTransform: 'none', fontWeight: 600, py: 1.2, borderColor: 'divider' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                disabled={isLoading}
                                sx={{ borderRadius: 3.5, textTransform: 'none', fontWeight: 700, py: 1.2, boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}` }}
                            >
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : "Create User"}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Grid>
    );
};

export default UsersUpdate;